import { useEffect, useState } from 'react';
import { useGlobal, useEnqueueEngagement } from '../../hooks';
import { trackSession } from './useTrackSession';
import { useAPIRoute } from '../../hooks/useAPIRoute';
import { Config } from '../ConfigFetcher/useConfig';
import { useTechnician } from '../../hooks/useTechnician';
import { ACTION_TYPE } from 'rwl-shared/dist/models/EngagementEvent';
import { usePublishEngagements } from '../../hooks/usePublishEngagement';
import { DeviceGeolocationStatus } from 'rwl-ui';
import { PluginGeoLocation } from 'rwl-shared/dist/models/plugin/Plugin';
import { useGeolocation } from '../../hooks/useGeolocation';

export const SessionTracker = (): null => {
    const [sessionId, setSessionId] = useState<string | undefined>(undefined);
    const { location, status } = useGeolocation();
    const { technician, isTechnicianReady } = useTechnician();
    const { isReady, accountId, config } = useGlobal();
    const url = useAPIRoute('session');
    const { enqueue } = useEnqueueEngagement();
    usePublishEngagements({ sessionId });
    function locationToGeoPoint(
        location: GeolocationCoordinates
    ): PluginGeoLocation {
        return {
            latitude: location.latitude,
            longitude: location.longitude,
        };
    }

    useEffect(() => {
        async function getGeoCoordinatesAndTrackSession(
            url: string,
            config: Config
        ) {
            const sessionId = await trackSession(
                config,
                url,
                'Unavailable',
                technician?.id
            );

            setSessionId(sessionId);

            // Once we have a session we can track the page view
            enqueue({
                actionType: ACTION_TYPE.VIEW,
                type: 'Engagement',
                target: 'Page View',
            });
        }

        if (isReady && config && isTechnicianReady) {
            getGeoCoordinatesAndTrackSession(
                url + '?accountId=' + accountId,
                config
            );
        }
    }, [isReady, config, isTechnicianReady]);

    // update the session with the geolocation when it is granted to us
    useEffect(() => {
        if (status !== DeviceGeolocationStatus.Available || !location) {
            return;
        }

        if (!config) {
            return;
        }
        const geoPoint = locationToGeoPoint(location);
        trackSession(
            config,
            url + '?accountId=' + accountId,
            geoPoint,
            technician?.id
        );
    }, [status]);

    // Track time spent on the portfolio
    // NOTE: This is poorly implemented, it will only capture page views that end in a refresh
    // if you close the tab the event might be enqueued into session storage but then your session
    // storage is trashed before the event can be published
    useEffect(() => {
        let startTime: number | undefined;
        const captureTime = () => {
            if (!startTime) {
                return;
            }

            const endTime = new Date().getTime();
            const duration = (endTime - startTime) / 1000;

            enqueue({
                actionType: ACTION_TYPE.TIMER,
                type: 'Engagement',
                target: 'Time Spent on Portfolio',
                duration,
            });
        };

        if (sessionId) {
            startTime = new Date().getTime();
            window.addEventListener('beforeunload', captureTime);
        }
        return () => {
            captureTime();
            window.removeEventListener('beforeunload', captureTime);
        };
    }, [sessionId]);

    return null;
};
